import React, { useContext, useMemo } from 'react';
import { node, string } from 'prop-types';
import useReducer from '@hooks/useReducer';
import { useFetch } from '@ajaibid/hooks';
import { getAvailableLot } from 'Component/__deprecated__/StockActionV2/helpers';
import { MarginTradingContext } from '@context/marginTrading';
import { getStockPortfolio, getStockPortfolioBonus } from '@services/api/stock/portfolio';
import stockService from '@services/stock';
import reducers, {
  INITIAL_STATE,
  RESET_POPUP,
  SELECT_ACCOUNT_TYPE,
  SELECT_PERCENTAGE,
  SELECT_PERIOD,
  SELECT_TAB_ORDER,
  SET_AUTO_ORDER_DATE,
  SET_AUTO_PRICE_CONDITION,
} from './popupReducers';
import useRealtimeStockPrice from '../websocket/useRealtimeStockPrice';

export const StockPopUpContext = React.createContext();

const StockPopUpProvider = ({ children, selectedCode }) => {
  const {
    data: stockData,
    isSuccess: loadingStock,
    refetch: fetchStockDetail,
  } = stockService.detail.about.useAbout(selectedCode);
  const { data: priceData, isDataReady: loadingPrice } = useRealtimeStockPrice(selectedCode);
  const fetchStockPrice = () => {}; // noop
  const { state, dispatchHelper } = useReducer(reducers, INITIAL_STATE);

  const {
    data: portfolioList,
    loading: loadingPortfolioList,
    fetchData: fetchPortfolioList,
  } = useFetch(getStockPortfolio, {}, false);
  const {
    data: portfolioBonus,
    loading: loadingPortfolioBonus,
    fetchData: fetchPortfolioBonus,
  } = useFetch(getStockPortfolioBonus, {}, false);

  const { mtPorto } = useContext(MarginTradingContext);
  const regPortfolio = portfolioList.result?.portfolio;
  const bonusPortfolio = portfolioBonus.results;
  const marginPortfolio = mtPorto.result?.portfolio;

  const availableBonusLot = useMemo(
    () => getAvailableLot(bonusPortfolio, selectedCode, 'code'),
    [portfolioBonus, selectedCode]
  );
  const availableRegLot = useMemo(
    () => getAvailableLot(regPortfolio, selectedCode, 'stock'),
    [regPortfolio, selectedCode]
  );
  const availableMarginLot = useMemo(
    () => getAvailableLot(marginPortfolio, selectedCode, 'stock'),
    [marginPortfolio, selectedCode]
  );

  /**
   * @param {string} accountType margin | reguler
   */
  const handleSelectAccountType = (accountType) => {
    dispatchHelper(SELECT_ACCOUNT_TYPE, accountType);
  };

  /**
   * @param {number} percentage 0.25 | 0.5 | 0.75 | 1
   */
  const handleSelectPercentage = (percentage) => {
    const value =
      percentage === state.selectedPercentage && !!state.selectedPercentage ? 0 : percentage;
    dispatchHelper(SELECT_PERCENTAGE, value);
  };

  /**
   * @param {string} period day | session
   */
  const handleSelectPeriod = (period) => {
    dispatchHelper(SELECT_PERIOD, period);
  };

  /**
   * @param {string} tabOrder limit | auto
   */
  const handleSelectTabOrder = (tabOrder) => {
    dispatchHelper(SELECT_TAB_ORDER, tabOrder);
  };

  const handleResetStockPopupContext = () => {
    dispatchHelper(RESET_POPUP);
  };

  /**
   * @param {number} value 0 (less than equal) | 1 (greater then equal)
   */
  const setAutoPriceCondition = (value) => {
    dispatchHelper(SET_AUTO_PRICE_CONDITION, value);
  };

  /**
   * @param {Date} startDate
   * @param {Date} endDate
   */
  const setAutoDate = ({ startDate, endDate }) => {
    dispatchHelper(SET_AUTO_ORDER_DATE, { startDate, endDate });
  };

  const valueProps = {
    ...state,
    availableBonusLot,
    availableMarginLot,
    availableRegLot,
    autoEndDate: state.autoEndDate,
    autoPriceCondition: state.autoPriceCondition,
    autoStartDate: state.autoStartDate,
    selectedAccountType: state.selectedAccountType,
    selectedPercentage: state.selectedPercentage,
    selectedPeriod: state.selectedPeriod,
    tabOrder: state.tabOrder,
    setAutoDate,
    setAutoPriceCondition,
    handleResetStockPopupContext,
    handleSelectAccountType,
    handleSelectPercentage,
    handleSelectPeriod,
    handleSelectTabOrder,
    fetchStockPrice,
    loadingPrice,
    priceData,
    fetchStockDetail,
    loadingStock,
    stockData,
    fetchPortfolioList,
    loadingPortfolioList,
    portfolioList,
    fetchPortfolioBonus,
    loadingPortfolioBonus,
    portfolioBonus,
  };

  return <StockPopUpContext.Provider value={valueProps}>{children}</StockPopUpContext.Provider>;
};

StockPopUpProvider.propTypes = {
  children: node.isRequired,
  selectedCode: string.isRequired,
};

export default StockPopUpProvider;
